import React from 'react';
import PropTypes from 'prop-types';
import AdyenEncrypt from 'adyen-cse-js';
import Button from '@strava/ui/Button';
import Spinner from '@strava/ui/Spinner';
import { trackV2 } from 'utils/analytics';

import I18n from 'utils/I18n';

import {
  isValidCardName,
  isValidCardNumber,
  isValidCardExpDate,
  isValidCardCvv
} from './validator';

import styles from './index.scss';
import {
  analyticsDataProps,
  isTrialProps
} from '../../apps/subscriptions/Checkout/shared/prop-types';
import { subscriptionType } from '../../apps/subscriptions/Checkout/utils/checkoutUtils';

const I18nPrefix = 'components.credit_card_form';
export const I18nKey = {
  required: `${I18nPrefix}.required`,
  name: `${I18nPrefix}.name`,
  number: `${I18nPrefix}.card_number`,
  expiration: `${I18nPrefix}.expiration`,
  cvv: `${I18nPrefix}.cvv`,
  error: {
    name: `${I18nPrefix}.error.card_name`,
    number: `${I18nPrefix}.error.card_number`,
    expiration: `${I18nPrefix}.error.card_expiration`,
    cvv: `${I18nPrefix}.error.card_cvv`
  },
  placeholder: {
    name: `${I18nPrefix}.placeholder.card_name`,
    number: `${I18nPrefix}.placeholder.card_number`,
    expiration_month: `${I18nPrefix}.placeholder.expiration_month`,
    expiration_year: `${I18nPrefix}.placeholder.expiration_year`
  }
};

class CreditCardForm extends React.Component {
  state = {
    name: '',
    number: '',
    cvv: '',
    expMonth: '',
    expYear: '',
    isValidName: null,
    isValidNumber: null,
    isValidExpDate: null,
    isValidCvv: null
  };

  cseInstance = AdyenEncrypt.createEncryption(this.props.adyenClientPubkey, {});

  handleSubmit = (e) => {
    e.preventDefault();

    // disabledInput is true when we have a full promotion code applied and
    // the user is not required to enter credit card. In that case, we need
    // to skip validation
    if (this.props.disabledInput) {
      this.props.submitHandler();
    } else if (this.isValidForm()) {
      const encryptCardData = this.cseInstance.encrypt({
        holderName: this.state.name,
        number: this.state.number,
        expiryMonth: this.state.expMonth,
        expiryYear: `20${this.state.expYear}`,
        cvc: this.state.cvv,
        generationtime: new Date().toISOString()
      });

      this.props.submitHandler(encryptCardData);
    } else {
      this.props.invalidHandler();
    }

    trackV2({
      category: 'subscriptions',
      page: this.props.isStudentPromotion
        ? 'student_plan_checkout'
        : 'checkout',
      element: 'trial_or_subscribe',
      action: 'click',
      properties: {
        ...this.props.analyticsData,
        subscription_type: subscriptionType(this.props.isTrial)
      }
    });
  };

  isValidForm = () => {
    const { name, number, expMonth, expYear, cvv } = this.state;

    const isValidName = isValidCardName(name);
    const isValidNumber = isValidCardNumber(number);
    const isValidExpDate = isValidCardExpDate(expMonth, expYear);
    const isValidCvv = isValidCardCvv(number, cvv);

    this.setState({
      isValidName,
      isValidNumber,
      isValidExpDate,
      isValidCvv
    });

    return isValidName && isValidNumber && isValidExpDate && isValidCvv;
  };

  hasError = (bool) => !(bool === null || bool);

  inputClassName = (input, bool) =>
    `${styles[input]} form-group ${this.hasError(bool) ? 'has-error' : ''}`;

  nameInputClassName = () =>
    this.inputClassName('name', this.state.isValidName);

  numberInputClassName = () =>
    this.inputClassName('number', this.state.isValidNumber);

  cvvInputClassName = () => this.inputClassName('cvv', this.state.isValidCvv);

  expMonthClassName = () =>
    this.inputClassName('expMonth', this.state.isValidExpDate);

  expYearClassName = () =>
    this.inputClassName('expYear', this.state.isValidExpDate);

  renderNameInput = () => (
    <div className={this.nameInputClassName()}>
      {this.props.showRequiredCopy && (
        <div className={styles.requiredCopy}>{I18n.t(I18nKey.required)}</div>
      )}
      <label className={styles.label} htmlFor="card-name">
        {I18n.t(I18nKey.name)}
      </label>
      <input
        id="card-name"
        className={`form-control ${styles.formControl}`}
        type="text"
        placeholder={I18n.t(I18nKey.placeholder.name)}
        autoComplete="off"
        disabled={this.props.disabledInput || this.props.disabledAll}
        value={this.state.name}
        onChange={(e) => this.setState({ name: e.target.value })}
      />
      {this.hasError(this.state.isValidName) && (
        <div
          data-test="name-error-message"
          className={styles.inputErrorMessage}
        >
          {I18n.t(I18nKey.error.name)}
        </div>
      )}
    </div>
  );

  renderCardNumberInput = () => (
    <div className={this.numberInputClassName()}>
      <label className={styles.label} htmlFor="card-number">
        {I18n.t(I18nKey.number)}
      </label>
      <input
        id="card-number"
        className={`form-control ${styles.formControl}`}
        type="text"
        placeholder={I18n.t(I18nKey.placeholder.number)}
        autoComplete="off"
        disabled={this.props.disabledInput || this.props.disabledAll}
        value={this.state.number}
        onChange={(e) => this.setState({ number: e.target.value })}
      />
      {this.hasError(this.state.isValidNumber) && (
        <div
          data-test="number-error-message"
          className={styles.inputErrorMessage}
        >
          {I18n.t(I18nKey.error.number)}
        </div>
      )}
    </div>
  );

  renderExpirationInputs = () => (
    <div className={styles.expirationDate}>
      <label className={styles.label} htmlFor="card-expiry-month">
        {I18n.t(I18nKey.expiration)}
      </label>
      <div className={styles.dates}>
        <div className={this.expMonthClassName()}>
          <input
            id="card-expiry-month"
            className={`form-control ${styles.formControl}`}
            type="text"
            placeholder={I18n.t(I18nKey.placeholder.expiration_month)}
            maxLength="2"
            autoComplete="off"
            disabled={this.props.disabledInput || this.props.disabledAll}
            value={this.state.expMonth}
            onChange={(e) => this.setState({ expMonth: e.target.value })}
          />
        </div>
        <span className={styles.dateDivider}>/</span>
        <div className={this.expYearClassName()}>
          <input
            id="card-expiry-year"
            className={`form-control ${styles.formControl}`}
            type="text"
            placeholder={I18n.t(I18nKey.placeholder.expiration_year)}
            maxLength="2"
            autoComplete="off"
            disabled={this.props.disabledInput || this.props.disabledAll}
            value={this.state.expYear}
            onChange={(e) => this.setState({ expYear: e.target.value })}
          />
        </div>
      </div>
      {this.hasError(this.state.isValidExpDate) && (
        <div
          data-test="expiration-error-message"
          className={styles.inputErrorMessage}
        >
          {I18n.t(I18nKey.error.expiration)}
        </div>
      )}
    </div>
  );

  renderCvvInput = () => (
    <div className={this.cvvInputClassName()}>
      <label className={styles.label} htmlFor="card-cvv">
        {I18n.t(I18nKey.cvv)}
      </label>
      <input
        id="card-cvv"
        className={`form-control ${styles.formControl}`}
        type="text"
        maxLength="4"
        autoComplete="off"
        disabled={this.props.disabledInput || this.props.disabledAll}
        value={this.state.cvv}
        onChange={(e) => this.setState({ cvv: e.target.value })}
      />
      {this.hasError(this.state.isValidCvv) && (
        <div data-test="cvv-error-message" className={styles.inputErrorMessage}>
          {I18n.t(I18nKey.error.cvv)}
        </div>
      )}
    </div>
  );

  renderSubmitButtonContent = () => {
    const { isSubmitting, ctaText } = this.props;

    if (isSubmitting) {
      return <Spinner color="white" />;
    }

    return (
      <>
        <span className={`app-icon-wrapper ${styles.iconLock}`}>
          <span className="app-icon icon-locked icon-white icon-sm" />
        </span>
        {ctaText}
      </>
    );
  };

  render() {
    const { disabledAll } = this.props;

    return (
      <form className={styles.form} onSubmit={this.handleSubmit}>
        {this.renderNameInput()}
        {this.renderCardNumberInput()}
        <div className={`${styles.cardMeta} ${styles.formGroup} form-group`}>
          {this.renderExpirationInputs()}
          {this.renderCvvInput()}
        </div>

        <Button
          type="submit"
          variant="primary"
          className={styles.submitBtn}
          disabled={disabledAll}
          data-testid="credit-card-form-btn"
        >
          {this.renderSubmitButtonContent()}
        </Button>
      </form>
    );
  }
}

CreditCardForm.propTypes = {
  adyenClientPubkey: PropTypes.string.isRequired,
  submitHandler: PropTypes.func,
  invalidHandler: PropTypes.func,
  isSubmitting: PropTypes.bool,
  disabledAll: PropTypes.bool,
  disabledInput: PropTypes.bool,
  ctaText: PropTypes.string.isRequired,
  showRequiredCopy: PropTypes.bool,
  analyticsData: analyticsDataProps.isRequired,
  isTrial: isTrialProps.isRequired,
  isStudentPromotion: PropTypes.bool.isRequired
};

CreditCardForm.defaultProps = {
  submitHandler: () => null,
  invalidHandler: () => null,
  isSubmitting: false,
  disabledAll: false,
  disabledInput: false,
  showRequiredCopy: true
};

export default CreditCardForm;
